import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Chip, Container, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelectedActivity, selectActivity, selectFindingActivityList } from '../../redux/reducers/activityReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import { selectFrameworkTitle } from '../../redux/reducers/frameworkReducer';
import { selectNameList } from '../../redux/reducers/organizationReducer';
import formatDate from '../../utils/dateHelpers';
import CorrectiveActionModal from '../activities/CorrectiveActionModal';
import ResolveFindingModal from './ResolveFindingModal';

function ActivityItem({ activity, handleEdit }) {
  const { t } = useTranslation();
  const names = useSelector((state) => selectNameList(state, activity.activityOwner));

  return (
    <Container maxWidth={false} disableGutters>
      <Paper
        elevation={4}
        variant="elevation"
        subtype="finding"
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1}>
              <Typography>{activity.title}</Typography>
              <Chip
                size="small"
                color="controlChip"
                label={t(`activity.status.${activity.activityStatus}`)}
              />
            </Stack>
            <Typography>{`${names} - ${t('activity.modal.deadline')} ${formatDate(activity.deadline)}`}</Typography>
          </Stack>
          <Button
            variant="text"
            size="small"
            name="editLink"
            onClick={() => handleEdit(activity.id)}
            endIcon={<EditIcon />}
          >
            {t('activity.modal.editActivity')}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

function ExpandableFinding({ finding }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const controlTitle = useSelector((state) => selectTitle(state, finding.controlId));
  const [expanded, setExpanded] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const activities = useSelector((state) => selectFindingActivityList(state, finding.id));
  const frameworkTitle = useSelector((state) => selectFrameworkTitle(state, finding.frameworkId));

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleResolveModalToggle = () => {
    setResolveModalOpen(!resolveModalOpen);
  };

  const handleActionModalToggle = () => {
    setActionModalOpen(!actionModalOpen);
  };

  const handleNewActivity = () => {
    dispatch(clearSelectedActivity());
    handleActionModalToggle();
  };

  const handleActivityEdit = (id) => {
    dispatch(selectActivity(id));
    handleActionModalToggle();
  };

  const getActivitiesText = () => (
    `${activities.length} activit${activities.length === 1 ? 'y' : 'ies'}`
  );

  return (
    <Paper elevation={4} sx={{ px: 2, py: 1, mx: 0.25, maxWidth: '900px' }}>
      {actionModalOpen && (
        <CorrectiveActionModal finding={finding} handleClose={handleActionModalToggle} />
      )}
      {resolveModalOpen && (
        <ResolveFindingModal finding={finding} handleClose={handleResolveModalToggle} />
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography noWrap>{finding.title}</Typography>
          <Typography variant="body2">{controlTitle}</Typography>
          <Typography variant="body2">{frameworkTitle}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          {finding.resolved ? (
            <Stack direction="row" spacing={1}>
              <CheckCircleOutlineOutlinedIcon color="primary" />
              <Typography color="primary">{t('finding.resolved')}</Typography>
            </Stack>
          ) : (
            activities.length > 0 && (
              <Chip
                key={activities[0].id}
                size="small"
                variant="filled"
                color="primary"
                label={<Typography variant="chip">{getActivitiesText()}</Typography>}
              />
            )
          )}
          <Chip
            size="medium"
            variant="filled"
            subtype={finding.criticality}
            label={<Typography variant="chip">{t(`finding.criticality.${finding.criticality}`)}</Typography>}
          />
          <IconButton onClick={() => handleExpand()}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Stack>
      </Stack>
      {expanded && (
        <Stack spacing={2} sx={{ mb: '16px' }}>
          <Divider />
          <Stack spacing={1}>
            <Stack>
              <Typography>{t('finding.description')}</Typography>
              <Typography variant="body2">{finding.description}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {finding.usualFindings.map((usualFinding) => (
                <Chip
                  key={usualFinding}
                  color="primary"
                  size="medium"
                  label={(
                    <Typography variant="chip">
                      {t(`finding.usualFinding.${usualFinding}`)}
                    </Typography>
                  )}
                />
              ))}
            </Stack>
          </Stack>
          <Divider />
          {!finding.resolved && (
            <Stack spacing={2}>
              <Typography variant="overline">{t('finding.corrective.title')}</Typography>
              <Typography variant="body2">{t('finding.corrective.description')}</Typography>
              <Button variant="outlined" onClick={() => handleResolveModalToggle()}>
                <Typography variant="buttonMedium">{t('finding.buttonResolve')}</Typography>
              </Button>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline">{t('finding.activities.title')}</Typography>
            {!finding.resolved && (
              <Button variant="outlined" endIcon={<AddIcon />} onClick={() => handleNewActivity()}>
                {t('activity.modal.newCorrectiveActivity')}
              </Button>
            )}
          </Stack>
          {activities.length === 0 && <Typography variant="body2">{t('finding.activities.noActivities')}</Typography>}
          {activities.map((activity) => <ActivityItem key={activity.id} activity={activity} handleEdit={handleActivityEdit} />)}
        </Stack>
      )}
    </Paper>
  );
}

export default ExpandableFinding;
